import { getStoredUser, getCurrentAccess } from './accessControl';

/**
 * Merge role base permissions with user-specific overrides.
 * Mirrors the backend logic.
 */
export const mergePermissions = (basePermissions = [], overrides = null) => {
  let finalPermissions = new Set(basePermissions || []);

  if (!overrides) return Array.from(finalPermissions);

  let parsedOverrides = {};
  if (typeof overrides === 'string') {
    try {
      parsedOverrides = JSON.parse(overrides);
    } catch {
      return Array.from(finalPermissions);
    }
  } else if (typeof overrides === 'object') {
    parsedOverrides = overrides;
  }

  if (Array.isArray(parsedOverrides.add)) {
    parsedOverrides.add.forEach((p) => finalPermissions.add(p));
  }
  if (Array.isArray(parsedOverrides.remove)) {
    parsedOverrides.remove.forEach((p) => finalPermissions.delete(p));
  }

  return Array.from(finalPermissions);
};

/**
 * Check if the user is a Super Admin based on the wildcard permission.
 */
export const isSuperAdmin = (user = getStoredUser()) => {
  const userPerms = Array.isArray(user?.permissions) ? user.permissions : [];
  return userPerms.includes('*');
};

/**
 * Evaluate if the current user has a specific permission.
 */
export const hasPermission = (permission, user = getStoredUser()) => {
  if (!permission) return true; // No permission required

  const userPerms = Array.isArray(user?.permissions) ? user.permissions : [];
  
  if (userPerms.includes('*')) return true;
  
  // Implicitly grant billing permissions to clinic admins and doctors since it's a new module
  if (permission.startsWith('billing:') && (getCurrentAccess(user).isClinicAdmin || getCurrentAccess(user).isDoctor)) {
    return true;
  }

  // If exact permission matches
  if (userPerms.includes(permission)) return true;

  // Hierarchical permission resolution: a 'manage' permission covers 'read'
  if (permission.includes(':read')) {
    const manageEquivalent = permission.replace(':read', ':manage');
    if (userPerms.includes(manageEquivalent)) return true;
  }

  // Clinic Admin & Doctor implicit operational access for all operational clinic modules
  if (getCurrentAccess(user).isClinicAdmin || getCurrentAccess(user).isDoctor) {
    if (
      permission.startsWith('lab_') ||
      permission.startsWith('inventory:') ||
      permission.startsWith('reports:') ||
      permission.startsWith('clinical_') ||
      permission.startsWith('records:') ||
      permission.startsWith('prescriptions:') ||
      permission.startsWith('medical_') ||
      permission.startsWith('billing:') ||
      permission.startsWith('pharmacy:')
    ) {
      return true;
    }
  }

  return false;
};

/**
 * Evaluate if the user has ANY of the required permissions
 */
export const hasAnyPermission = (permissions, user = getStoredUser()) => {
  if (!permissions || permissions.length === 0) return true;
  if (!Array.isArray(permissions)) return hasPermission(permissions, user);

  return permissions.some((perm) => hasPermission(perm, user));
};

/**
 * Evaluate if the user has ALL of the required permissions
 */
export const hasAllPermissions = (permissions, user = getStoredUser()) => {
  if (!permissions || permissions.length === 0) return true;
  if (!Array.isArray(permissions)) return hasPermission(permissions, user);

  return permissions.every((perm) => hasPermission(perm, user));
};
